import type { NavigateFunction } from 'react-router-dom'

import { PlusIcon } from '@buffer-mono/popcorn'

import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

/**
 * Service display names and their search aliases.
 * When query matches a service, we show a specific "Connect new {service} channel" action.
 */
const SUPPORTED_SERVICES: Record<
  string,
  { displayName: string; aliases: string[] }
> = {
  instagram: {
    displayName: 'Instagram',
    aliases: ['ig', 'insta', 'gram'],
  },
  facebook: {
    displayName: 'Facebook',
    aliases: ['fb', 'meta'],
  },
  twitter: {
    displayName: 'X (Twitter)',
    aliases: ['x', 'tweet', 'tweets'],
  },
  linkedin: {
    displayName: 'LinkedIn',
    aliases: ['li', 'linked'],
  },
  pinterest: {
    displayName: 'Pinterest',
    aliases: ['pin', 'pins'],
  },
  tiktok: {
    displayName: 'TikTok',
    aliases: ['tt', 'tok'],
  },
  youtube: {
    displayName: 'YouTube',
    aliases: ['yt', 'video'],
  },
  threads: {
    displayName: 'Threads',
    aliases: ['thread'],
  },
  mastodon: {
    displayName: 'Mastodon',
    aliases: ['masto', 'fedi', 'fediverse'],
  },
  bluesky: {
    displayName: 'Bluesky',
    aliases: ['bsky', 'sky'],
  },
  googlebusiness: {
    displayName: 'Google Business',
    aliases: ['google', 'gbp', 'gmb', 'business'],
  },
}

/**
 * Find matching service based on query.
 * Returns the service key if query matches service name or alias.
 */
function findMatchingService(query: string): string | null {
  if (!query) return null

  const normalizedQuery = query.toLowerCase().trim()

  for (const [serviceKey, { displayName, aliases }] of Object.entries(
    SUPPORTED_SERVICES,
  )) {
    if (
      serviceKey === normalizedQuery ||
      displayName.toLowerCase() === normalizedQuery ||
      aliases.includes(normalizedQuery)
    ) {
      return serviceKey
    }
  }

  return null
}

/**
 * Options for building connect channel actions.
 */
interface BuildConnectChannelActionsOptions {
  /** React Router navigate function for navigation */
  navigate: NavigateFunction
  /** Current search query to match against services */
  query?: string
}

/**
 * Builds the "Connect new channel" action(s).
 *
 * When query matches a specific service (e.g., "instagram"), shows a service-specific action.
 * Otherwise shows the generic "Connect new channel" action.
 *
 * @param options - Configuration options
 * @param options.navigate - React Router navigate function
 * @param options.query - Current search query to match against services
 * @returns Array of connect channel actions
 */
export function buildConnectChannelActions({
  navigate,
  query = '',
}: BuildConnectChannelActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  const matchingService = findMatchingService(query)

  if (matchingService) {
    const service = SUPPORTED_SERVICES[matchingService]
    actions.push(
      createAction({
        id: `connect-channel-${matchingService}`,
        name: `Connect a new ${service.displayName} channel`,
        keywords: [
          'connect',
          'add',
          'new channel',
          'link',
          'setup',
          'configure',
          matchingService,
          service.displayName.toLowerCase(),
          ...service.aliases,
        ],
        icon: <PlusIcon />,
        section: 'connect-channel',
        priority: PRIORITIES.connectService,
        perform: () => {
          navigate('/settings/channels')
        },
      }),
    )
  }

  // Always show the generic connect action
  actions.push(
    createAction({
      id: 'connect-channel',
      name: 'Connect new channel',
      keywords: [
        'connect',
        'add',
        'new channel',
        'link',
        'setup',
        'configure',
        'social',
        'account',
      ],
      icon: <PlusIcon />,
      section: 'connect-channel',
      priority: PRIORITIES.connectChannel,
      description: 'Add a new social media account',
      perform: () => {
        navigate('/settings/channels')
      },
    }),
  )

  return actions
}
